import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { FileText } from 'lucide-react';
import Button from '../ui/Button';
import { useAuth } from '../../context/AuthContext';
import { acceptTerms, getCurrentTerms, type PlatformTermsCurrent } from '../../services/platformTerms';
import { ApiError } from '../../types/api';
import { formatLocalDateInTz } from '../../utils/timezone';
import TermsSummary from './TermsSummary';

/** Where the dialog must not cover the page: the text it asks you to read. */
const READING_PATHS = ['/terms', '/privacy'];

/**
 * Asks an admin of the user's active organisation to accept the current Terms
 * and Conditions for it, once per published version. Everyone else is never
 * asked: the Terms bind the Organisation, and a picker cannot bind their
 * employer. One admin's acceptance covers the whole organisation. Mounted
 * once in App, above the routes.
 *
 * It fails open: if the check cannot be made (offline in a paddock, a backend
 * that has not deployed the endpoint yet) nothing is shown. Locking a field
 * worker out of their timesheet because a request failed is worse than asking
 * them on the next launch.
 */
function PlatformTermsGate() {
  const { user, isAuthenticated, logout } = useAuth();
  const orgId = user?.organisation_id;
  const { pathname } = useLocation();
  const queryClient = useQueryClient();
  const queryKey = ['platform-terms', user?.id, orgId];

  const { data: terms } = useQuery({
    queryKey,
    queryFn: async () => {
      const res = await getCurrentTerms();
      return res.success ? res.data : null;
    },
    enabled: isAuthenticated && !!user,
    staleTime: Infinity,
    retry: false,
  });

  if (!isAuthenticated || !terms || terms.hasAccepted || !terms.canAccept || !terms.version) return null;
  if (READING_PATHS.includes(pathname)) return null;

  return (
    <PlatformTermsDialog
      terms={terms}
      organisationName={user?.organisation_name}
      onAccepted={() => queryClient.setQueryData(queryKey, { ...terms, hasAccepted: true })}
      onStale={() => queryClient.invalidateQueries({ queryKey })}
      onSignOut={logout}
    />
  );
}

interface DialogProps {
  terms: PlatformTermsCurrent;
  organisationName?: string;
  onAccepted: () => void;
  /** The version moved while the dialog was open: reload it. */
  onStale: () => void;
  onSignOut: () => Promise<void>;
}

export function PlatformTermsDialog({ terms, organisationName, onAccepted, onStale, onSignOut }: DialogProps) {
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleAccept = async () => {
    if (!agreed || !terms.version) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await acceptTerms(terms.version);
      if (res.success) {
        onAccepted();
        return;
      }
      setError('Could not record your acceptance. Please try again.');
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setError('The Terms were updated while you were reading. The latest version is loading.');
        onStale();
      } else {
        setError('Could not record your acceptance. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="platform-terms-title"
    >
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        <header className="px-4 sm:px-6 py-4 border-b border-neutral-200 flex items-start gap-3">
          <FileText className="h-5 w-5 text-primary-600 mt-0.5 shrink-0" />
          <div className="min-w-0">
            <h2 id="platform-terms-title" className="text-lg font-semibold text-neutral-900">
              Terms and Conditions
            </h2>
            <p className="text-xs text-neutral-500">
              Version {terms.version}
              {terms.effective_date && (
                <> &middot; effective {formatLocalDateInTz(terms.effective_date, 'Australia/Melbourne')}</>
              )}
            </p>
          </div>
        </header>

        <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 space-y-4">
          <p className="text-sm text-neutral-700 leading-relaxed">
            As an administrator of {organisationName || 'your organisation'}, you are asked to accept these once
            for everyone who uses FactreeTech under its account.
          </p>
          <TermsSummary />
          <p className="text-sm text-neutral-700 leading-relaxed">
            Please read the full{' '}
            <Link to="/terms" className="text-primary-600 hover:text-primary-700 font-medium">
              Terms and Conditions
            </Link>{' '}
            and{' '}
            <Link to="/privacy" className="text-primary-600 hover:text-primary-700 font-medium">
              Privacy Policy
            </Link>{' '}
            before accepting. This will be waiting for you when you come back.
          </p>
        </div>

        <footer className="px-4 sm:px-6 py-4 border-t border-neutral-200 bg-neutral-50 rounded-b-xl space-y-3">
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
              className="mt-0.5 h-4 w-4 shrink-0 rounded border-neutral-300 text-primary-600 focus:ring-primary-500"
            />
            <span className="text-sm text-neutral-700">
              I have read and agree to the Terms and Conditions on behalf of{' '}
              <strong>{organisationName || 'my organisation'}</strong>, and I am authorised to accept them for it.
            </span>
          </label>
          {error && <p className="text-sm text-rose-600">{error}</p>}
          <div className="flex flex-wrap justify-end gap-2">
            <Button variant="outline" onClick={() => void onSignOut()} disabled={submitting}>
              Sign out
            </Button>
            <Button onClick={handleAccept} disabled={!agreed || submitting} isLoading={submitting}>
              Accept and continue
            </Button>
          </div>
        </footer>
      </div>
    </div>
  );
}

export default PlatformTermsGate;
